import { Node, mergeAttributes } from "@tiptap/core";

/**
 * Inline atom node for LaTeX spacing primitives.
 *
 * Stores a single LaTeX command (\hspace / \hfill / \vspace / \smallskip
 * / \medskip / \bigskip / \vfill) as a discrete inline node, so the
 * editor canvas can render a visible gap or arrow badge instead of
 * showing the raw backslash command as literal text.
 *
 * Serialisation (in document-serializer.ts / content-converter.ts)
 * walks this node and emits the appropriate LaTeX command back into
 * the stored text. Parse direction recognises the same commands and
 * produces a node on doc load.
 *
 * Why one node for both horizontal and vertical: keeps the ribbon
 * insertion logic uniform and lets users put a \smallskip mid-
 * paragraph (LaTeX is fine with that — emits the skip at the next
 * paragraph break). User direction 2026-05-14: spacing is light-
 * weight insertion, not a block type.
 */

export type SpacingKind =
  | "hspace"
  | "hfill"
  | "vspace"
  | "smallskip"
  | "medskip"
  | "bigskip"
  | "vfill";

interface SpacingAttrs {
  kind: SpacingKind;
  /** Length argument for hspace / vspace (e.g. "1em", "2cm"). Ignored
   *  for hfill / vfill / *skip commands which carry no arg. */
  arg: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    spacingCommand: {
      insertSpacing: (attrs: SpacingAttrs) => ReturnType;
    };
  }
}

// Visual hints rendered inside the badge — kept terse so the badge
// stays small. The arrow direction matches the spacing axis.
const KIND_GLYPHS: Record<SpacingKind, string> = {
  hspace: "→",
  hfill: "↔",
  vspace: "↓",
  smallskip: "↓",
  medskip: "↓",
  bigskip: "⇓",
  vfill: "↕",
};

function labelFor(attrs: SpacingAttrs): string {
  switch (attrs.kind) {
    case "hspace":
      return attrs.arg || "1em";
    case "vspace":
      return attrs.arg || "1em";
    case "hfill":
      return "fill";
    case "vfill":
      return "fill";
    case "smallskip":
      return "sm";
    case "medskip":
      return "md";
    case "bigskip":
      return "lg";
  }
}

export const SpacingCommand = Node.create({
  name: "spacingCommand",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      kind: {
        default: "hspace",
        parseHTML: (el) => (el.getAttribute("data-kind") as SpacingKind) || "hspace",
        renderHTML: (attrs) => ({ "data-kind": attrs.kind }),
      },
      arg: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-arg") || "",
        renderHTML: (attrs) => ({ "data-arg": attrs.arg }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-spacing]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const attrs = node.attrs as SpacingAttrs;
    const glyph = KIND_GLYPHS[attrs.kind] ?? "·";
    const label = labelFor(attrs);
    // Inline-block badge — narrow, tinted, monospace. Acts as a
    // visual cue ("there's a space here") without claiming a lot of
    // canvas space.
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-spacing": "true",
        contenteditable: "false",
        title: `\\${attrs.kind}${attrs.arg ? `{${attrs.arg}}` : ""}`,
        class: "lml-spacing",
        style: [
          "display: inline-flex",
          "align-items: center",
          "gap: 2px",
          "padding: 0 4px",
          "margin: 0 1px",
          "border-radius: 3px",
          "background: var(--lilia-surface, #f1f5f9)",
          "color: var(--lilia-text-muted, #6b7280)",
          "font-family: ui-monospace, SFMono-Regular, monospace",
          "font-size: 0.75em",
          "line-height: 1.4",
          "user-select: none",
          "vertical-align: baseline",
        ].join(";"),
      }),
      ["span", { "aria-hidden": "true" }, glyph],
      ["span", {}, label],
    ];
  },

  addCommands() {
    return {
      insertSpacing:
        (attrs: SpacingAttrs) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs }),
    };
  },
});

export default SpacingCommand;
